/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, NgZone, inject} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import * as _ from 'lodash';
import {firstValueFrom} from 'rxjs';
import template from './third-party-audience-edit-page.component.html';

import {AppSharedModule} from '../common/app-shared.module';
import {AdnSelectComponent} from '../common/directives/adn-select/adn-select.component';
import {AdnSelectSearchElement} from '../common/directives/adn-select/adn-select.component';
import {SignalAdminResourceEditPageBase} from '../common/controller/signal-admin-resource-edit-page-base';
import {AngularModalService} from '../common/modal/angular-modal.service';
import {ThirdPartyAudiencePostcodeModalComponent} from './third-party-audience-postcode-modal.component';
import {parsePostcodeUploadText} from './third-party-audience-postcode-upload';
import {TeamApiService} from '../admin/team/team-api.service';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {
  ThirdPartyAudienceApiService,
  ThirdPartyAudienceModel
} from './third-party-audience-api.service';

const DEFAULT_PROVIDER = 'LC_MEDIA';

@Component({
  standalone: true,
  imports: [AppSharedModule, FormsModule, AdnSelectComponent],
  selector: 'adn-third-party-audience-edit-page',
  template
})
export class ThirdPartyAudienceEditPageComponent extends SignalAdminResourceEditPageBase<ThirdPartyAudienceModel> {
  readonly providers = ['NORDIC_DATA_RESOURCES', 'LC_MEDIA'];

  blockSubmit: string | false = false;
  audienceData: string | null = null;
  teamSearchResults: any[] = [];

  private readonly audienceApi: ThirdPartyAudienceApiService;

  constructor(
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly teamApi: TeamApiService,
    private readonly toastr: ToastrService,
    private readonly angularModalService: AngularModalService,
    private readonly ngZone: NgZone
  ) {
    const api = inject(ThirdPartyAudienceApiService);
    super(api, 'thirdPartyAudience');
    this.audienceApi = api;
  }

  protected override initialiseModel(model: ThirdPartyAudienceModel): ThirdPartyAudienceModel {
    const initialised = super.initialiseModel(model);
    initialised.provider = initialised.provider || DEFAULT_PROVIDER;
    this.updateReadOnly(initialised);
    this.syncAudienceData(undefined, initialised);
    return initialised;
  }

  protected override afterSave(model: ThirdPartyAudienceModel): void {
    this.updateReadOnly(model);
    this.syncAudienceData(undefined, model);
  }

  canUploadPostcodes(): boolean {
    return !this.isNew && !this.readOnly && !!this.editingModel.id;
  }

  openPostcodeUploadModal(): void {
    if (!this.canUploadPostcodes()) {
      return;
    }

    const modalRef = this.angularModalService.show(ThirdPartyAudiencePostcodeModalComponent, {
      class: 'modal-lg adnDialog',
      initialState: {
        submitPostcodes: (text: string, countryIsoCode: string) => this.submitPostcodes(text, countryIsoCode)
      }
    });

    const modalContent = modalRef.content as ThirdPartyAudiencePostcodeModalComponent;
    modalContent.onClosed.subscribe(() => {
      this.ngZone.run(() => {
        this.syncAudienceData();
      });
    });
  }

  teamSearch = (searchElement: AdnSelectSearchElement): void => {
    this.teamApi.listOptions({
      minimal: true,
      filterByLike: searchElement.search,
      filterBy: 'name',
      teamTypes: 'AX_ADVERTISER',
      pageSize: 100
    }).subscribe((page) => {
      this.ngZone.run(() => {
        this.teamSearchResults = _.filter(page.results, (team: any) => team.type === 'AX_ADVERTISER');
      });
    });
  };

  private submitPostcodes(text: string, countryIsoCode: string): Promise<any> {
    const postcodes = parsePostcodeUploadText(text);

    if (!postcodes.length) {
      return Promise.reject(new Error('No postcodes were found in the entered text.'));
    }

    return firstValueFrom(this.audienceApi.uploadPostcodes(this.editingModel.id, postcodes, countryIsoCode)).then((response: any) => {
      this.toastr.success('Postcodes uploaded successfully.', 'Third-Party Audience');
      this.audienceData = text;
      return response;
    });
  }

  private updateReadOnly(model: ThirdPartyAudienceModel): void {
    let readOnly: boolean;
    if (this.LocalUserPermissions.hasNetworkPermission('MANAGE_SYSTEM')) {
      readOnly = false;
    } else if (this.isNew) {
      readOnly = !this.LocalUserPermissions.hasAny('MANAGE_AUDIENCES');
    } else {
      const teams = model.teams || [];
      readOnly = _.isEmpty(teams) || _.some(teams, (team: any) => {
        const teamId = _.isObject(team) ? (team as any).id : team;
        return !this.LocalUserPermissions.has('MANAGE_AUDIENCES', teamId);
      });
    }
    this.state.setReadOnly(readOnly);
    this.blockSubmit = this.readOnly ? 'You do not have permission to update this third party audience.' : false;
  }

  private syncAudienceData(text?: string, model: ThirdPartyAudienceModel = this.editingModel): void {
    if (text) {
      this.audienceData = text;
      return;
    }

    if (model.provider && model.metaData) {
      this.audienceData = _.get(model.metaData, model.provider) || null;
      return;
    }

    this.audienceData = null;
  }
}
